/**
 * @file TransferHeader.tsx
 * @description Шапка документа «Перемещение»: статус, основание, плитки meta и
 * действия (phase19.23 этап B; конвенция действий — phase19.24 этап 1, шаг 1;
 * phase19.31 зона «Склад» — шапка в верхней панели, `WmsDocumentPageHeader`).
 *
 * Одна главная кнопка и «⋯»: «Отменить» и звук скана — в меню, причина
 * блокировки («Добавьте хотя бы одну строку») — подписью; на телефоне
 * главная кнопка — в общей нижней полосе каркаса. Саму причину шапка не
 * считает — её приносит страница (`commitHint`).
 *
 * Отмены ПРОВЕДЁННОГО документа здесь нет намеренно (W15): движения уже в
 * журнале, ошибку правят вторым документом. Отмена черновика — через
 * подтверждение (`TransferCancelDialog`): промах пальцем не стирает документ.
 * «Отменено» — красным (SC26).
 */

import { Check, XCircle } from "lucide-react";
import { useState } from "react";

import { TransferCancelDialog } from "./TransferCancelDialog";
import { TransferNotePrintButton } from "./TransferNotePrintButton";
import { Button } from "../../ui/button";
import type { PageAction } from "../../ui/page-actions";
import { StatusPill } from "../../ui/StatusPill";
import { useScanSoundAction } from "../useScanSoundAction";
import { WmsDocumentPageHeader } from "../WmsDocumentPageHeader";
import { buildDocumentMeta } from "../wms-document-meta";
import type { TransferDto } from "../../../lib/api-transfers";
import { WMS_DOCUMENT_STATUS_META } from "../../../lib/shop/status-meta";

interface Props {
  transfer: TransferDto;
  /** Реестр перемещений — «назад». */
  backTo: string;
  /** Почему нельзя провести; `null` — документ готов. */
  commitHint: string | null;
  /** Есть межскладские строки → накладную печатать можно. */
  hasInterWarehouse: boolean;
  busy: boolean;
  committing: boolean;
  onCommit: () => void;
  onCancel: () => void;
}

export function TransferHeader({ transfer, backTo, commitHint, hasInterWarehouse, busy, committing, onCommit, onCancel }: Props) {
  const isDraft = transfer.status === "DRAFT";
  const items = transfer.items ?? [];
  const fromName = transfer.fromWarehouse?.name ?? transfer.fromWarehouseName ?? "—";
  const scanSound = useScanSoundAction();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const menu: PageAction[] = [{ ...scanSound, disabled: scanSound.disabled || busy || committing }];
  if (isDraft) {
    // Опасное действие — последним в меню (правило A6).
    menu.push({
      key: "cancel",
      label: "Отменить перемещение",
      icon: XCircle,
      danger: true,
      disabled: busy || committing,
      run: () => setConfirmCancel(true),
    });
  }

  const action = isDraft ? (
    <Button variant="brand" onClick={onCommit} disabled={committing || !!commitHint} loading={committing}>
      <Check className="size-4" />
      Провести перемещение
    </Button>
  ) : transfer.status === "COMMITTED" && hasInterWarehouse ? (
    // Накладная нужна только межскладскому переезду: у перестановок по
    // ячейкам сервер ответит `transfer_internal_only`.
    <TransferNotePrintButton transferId={transfer.id} number={transfer.transferNumber} />
  ) : undefined;

  return (
    <WmsDocumentPageHeader
      number={transfer.transferNumber}
      status={<StatusPill meta={WMS_DOCUMENT_STATUS_META[transfer.status]} />}
      subtitle={[`Откуда: ${fromName}`, transfer.receipt ? `основание — приёмка ${transfer.receipt.receiptNumber}` : null]
        .filter(Boolean)
        .join(" · ")}
      back={{ to: backTo, label: "К перемещениям" }}
      meta={buildDocumentMeta({
        authorLabel: "Создал",
        author: transfer.createdBy?.displayName,
        createdAt: transfer.createdAt,
        committedAt: transfer.committedAt,
        items,
      })}
      action={action}
      menu={menu}
      menuLabel="Действия с перемещением"
      hint={isDraft ? commitHint : null}
      dialogs={
        <TransferCancelDialog
          open={confirmCancel}
          number={transfer.transferNumber}
          busy={busy || committing}
          onCancel={() => setConfirmCancel(false)}
          onConfirm={() => {
            setConfirmCancel(false);
            onCancel();
          }}
        />
      }
    />
  );
}
